import { ColumnSizingState } from '@tanstack/react-table';

import { LocalStorageKey } from '@/shared/config';
import { safeLocalStorage } from '@/shared/lib';

import {
  PATIENT_LIST_COLUMNS,
  PATIENT_LIST_DEFAULT_COLUMN_SETTINGS,
  PATIENT_LIST_DEFAULT_COLUMN_VISIBILITY,
  PATIENT_LIST_TOGGLEABLE_COLUMNS,
  PatientListColumnSettings,
  PatientListColumnVisibility,
} from '../config';

const storageKey = (organizationID: string) =>
  `${LocalStorageKey.patientListColumns}.${organizationID}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Storage may hold an older shape or hand edits, so nothing in it is trusted.
const sanitize = (value: unknown): PatientListColumnSettings => {
  if (!isRecord(value)) {
    return PATIENT_LIST_DEFAULT_COLUMN_SETTINGS;
  }

  const rawVisibility = isRecord(value.visibility) ? value.visibility : {};
  const rawWidths = isRecord(value.widths) ? value.widths : {};

  const visibility: PatientListColumnVisibility = {
    ...PATIENT_LIST_DEFAULT_COLUMN_VISIBILITY,
    ...Object.fromEntries(
      PATIENT_LIST_TOGGLEABLE_COLUMNS.flatMap((column) =>
        typeof rawVisibility[column] === 'boolean'
          ? [[column, rawVisibility[column]]]
          : [],
      ),
    ),
  };

  const widths: ColumnSizingState = Object.fromEntries(
    PATIENT_LIST_COLUMNS.flatMap((column) => {
      const width = rawWidths[column];

      return typeof width === 'number' && Number.isFinite(width) && width > 0
        ? [[column, width]]
        : [];
    }),
  );

  return { visibility, widths };
};

export const readPatientListColumnSettings = (
  organizationID: string,
): PatientListColumnSettings => {
  if (!organizationID) {
    return PATIENT_LIST_DEFAULT_COLUMN_SETTINGS;
  }

  const raw = safeLocalStorage.getItem(storageKey(organizationID));

  if (raw === null) {
    return PATIENT_LIST_DEFAULT_COLUMN_SETTINGS;
  }

  try {
    return sanitize(JSON.parse(raw));
  } catch {
    return PATIENT_LIST_DEFAULT_COLUMN_SETTINGS;
  }
};

export const writePatientListColumnSettings = (
  organizationID: string,
  settings: PatientListColumnSettings,
) => {
  if (organizationID) {
    safeLocalStorage.setItem(
      storageKey(organizationID),
      JSON.stringify(settings),
    );
  }
};

export const removePatientListColumnSettings = (organizationID: string) => {
  if (organizationID) {
    safeLocalStorage.removeItem(storageKey(organizationID));
  }
};
